/**
 * Scoreboard formula — shared by the client (instant display) and the
 * Worker (authoritative recompute), so it must stay dependency-free.
 *
 * One board for the whole game. Players are ranked by how far they got
 * (tier), then by how fast they finished it (finish time), then by score:
 *
 *   tier  0 = on level 1 · 1 = finished level 1 · 2 = finished level 2
 *   finish time = level 1 time (tier 1) or level 1 + level 2 time (tier 2)
 *   score = best biomass per level ÷ 100 (capped at each goal)
 *         + 20 per DNA collected + 500 per achievement
 *         + speed bonus per finished level (10,000 at par, more if faster)
 */

export type ScoredStageId = 'cell' | 'multicell';

export const GOALS: Record<ScoredStageId, number> = { cell: 250_000, multicell: 3_000_000 };
export const PAR_SEC: Record<ScoredStageId, number> = { cell: 20 * 60, multicell: 40 * 60 };
export const SCORE_WEIGHTS = { biomassPerPoint: 100, dna: 20, achievement: 500, speed: 10_000 };

export function isScoredStage(id: string): id is ScoredStageId {
  return id === 'cell' || id === 'multicell';
}

/** A playthrough's best-ever progress. Only ever grows. */
export interface ScoreProgress {
  cellMaxBiomass: number;
  cellTimeSec: number | null;
  multicellMaxBiomass: number;
  multicellTimeSec: number | null;
  dna: number;
  achievements: number;
}

export function tierOf(p: ScoreProgress): 0 | 1 | 2 {
  if (p.multicellTimeSec != null) return 2;
  if (p.cellTimeSec != null) return 1;
  return 0;
}

/** Total time to finish the furthest finished level; null while on level 1. */
export function finishTimeOf(p: ScoreProgress): number | null {
  if (p.cellTimeSec == null) return null;
  return p.multicellTimeSec == null ? p.cellTimeSec : p.cellTimeSec + p.multicellTimeSec;
}

/** Board order: negative when `a` ranks above `b` (tier, then finish time, then score). */
export function compareProgress(a: ScoreProgress, b: ScoreProgress): number {
  const tier = tierOf(b) - tierOf(a);
  if (tier !== 0) return tier;
  const fa = finishTimeOf(a);
  const fb = finishTimeOf(b);
  if (fa != null && fb != null && fa !== fb) return fa - fb;
  return computeScore(b) - computeScore(a);
}

function speedBonus(stage: ScoredStageId, timeSec: number | null): number {
  if (timeSec == null) return 0;
  return Math.round(SCORE_WEIGHTS.speed * PAR_SEC[stage] / Math.max(1, timeSec));
}

export function computeScore(p: ScoreProgress): number {
  const biomass =
    Math.floor(Math.min(p.cellMaxBiomass, GOALS.cell) / SCORE_WEIGHTS.biomassPerPoint) +
    Math.floor(Math.min(p.multicellMaxBiomass, GOALS.multicell) / SCORE_WEIGHTS.biomassPerPoint);
  return biomass
    + Math.floor(p.dna) * SCORE_WEIGHTS.dna
    + Math.floor(p.achievements) * SCORE_WEIGHTS.achievement
    + speedBonus('cell', p.cellTimeSec)
    + speedBonus('multicell', p.multicellTimeSec);
}
